// © Microsoft Corporation. All rights reserved.

import { FluentButton, FluentDialog } from "@fluentui/web-components";
import { DOM, FASTElement, observable } from "@microsoft/fast-element";
import { keyCodeEscape } from "@microsoft/fast-web-utilities";
import { MsnMarketSelectorProps } from "@msnews/msn-market-selector";
import { SettingsLocalizedStrings, SettingsTelemetryContext } from "./Interfaces";
import { TelemetryDOM } from "@msnews/telemetry-core";

export class Settings extends FASTElement {
    /** Flag for ZH-CN domain **/
    @observable public isCnDomain: boolean;

    /** Localized strings object */
    @observable public localizedStrings: SettingsLocalizedStrings = {};

    /** Callback to navigate to "Personalize" */
    @observable public goToPersonalize?: () => void = () => {};

    /** Flag if the settings dialog should be visible */
    @observable public isSettingsDialogVisible = false;

    /** Settings telemetry context */
    @observable public telemetryContext: SettingsTelemetryContext = {};

    /** Market selector props */
    @observable public marketSelectorProps: MsnMarketSelectorProps = {} as MsnMarketSelectorProps;

    /** Selected market */
    @observable public selectedMarketId: string;

    /** Override styles */
    @observable public settingsOverrideStyles: string;

    /** Settings button ref */
    public settingsButton: FluentButton;

    /** Close button ref */
    public closeButton: FluentButton;

    /** Dialog ref */
    public dialogRef: FluentDialog;

    /**
     * Sets the settings dialog visible state
     * @param isSettingsDialogVisible new isSettingsDialogVisible value
     */
    public toggleSettingsDialog = (isSettingsDialogVisible: boolean): void => {
        this.isSettingsDialogVisible = isSettingsDialogVisible;

        if (isSettingsDialogVisible) {
            document.addEventListener("keydown", this.onKeydown);
            this.setDialogPosition();

            if (this.closeButton) {
                DOM.queueUpdate(() => this.closeButton.focus());
            }
        } else {
            document.removeEventListener("keydown", this.onKeydown);

            if (this.dialogRef) {
                this.setIsCaptured(this.dialogRef.children);
            }
        }
    };

    /**
     * Callback for when the personalize settings button is clicked
     */
    public onPersonalizeClick = (): void => {
        this.toggleSettingsDialog(false);
        this.goToPersonalize();
    };

    /**
     * Handler for key down events
     * @param event keyboard event
     */
    private onKeydown = (event: KeyboardEvent): void => {
        if (event.keyCode === keyCodeEscape) {
            this.toggleSettingsDialog(false);
        }
    };

    /**
     * Helper for setting position styles for settings dialog
     */
    private setDialogPosition(): void {
        const refPosition = this.settingsButton.getBoundingClientRect();

        const verticalPosition = `top: ${refPosition.bottom}px;`;
        let horizontalPosition;
        if (document.dir === "rtl") {
            horizontalPosition = `left: ${refPosition.left}px`;
        } else {
            horizontalPosition = `left: ${refPosition.left + refPosition.width - 320}px`;
        }

        this.settingsOverrideStyles = `.settings-dialog::part(control) {position: absolute; ${verticalPosition} ${horizontalPosition}}`;
    }

    /**
     * Reset the telemetry props
     */
    private setIsCaptured(elements: any): void {
        elements && Array.from(elements).forEach((element: HTMLElement) => {
            const obj = TelemetryDOM.get(element);
            if (obj) {
                obj.isCaptured = false;
                return;
            }

            if (element.children && element.children.length > 0) {
                this.setIsCaptured(element.children);
            }

            if (element.shadowRoot &&
                element.shadowRoot.children &&
                element.shadowRoot.children.length > 0) {
                this.setIsCaptured(element.shadowRoot.children);
            }
        });
    }
}